import { type ReactElement, useMemo } from 'react';

import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/react';

import { formatNumber, formatString, isDefined, isOfType } from '@enterprise/core';

import type { VirtualSelectProps, VirtualSelectPropsThreshold } from './types/virtual-select.props';

import { AsyncSelect } from './async.component';
import CheckboxOption from './components/checkbox-option.component';
import GroupHeading from './components/group-heading.component';
import Group from './components/group.component';
import Option from './components/option.component';
import VirtualMenuList from './components/virtual-menu-list.component';
import { Select } from './select.component';
import { calculateLength } from './utilities/calculate-length.util';

export function VirtualSelect<IsMulti extends boolean = false, Option = unknown>(
  props: VirtualSelectProps<IsMulti, Option>
) {
  const { components, options } = props;
  const count = useMemo(() => {
    return calculateLength(options);
  }, [options]);
  let Component: ReactElement = (
    <Select
      {...props}
      captureMenuScroll={false}
      components={{ MenuList: VirtualMenuList }}
    />
  );

  if (isOfType<VirtualSelectPropsThreshold<IsMulti, Option>>(props, 'threshold')) {
    const { filterOptions, threshold, thresholdWarningMessage, useCheckboxOption } = props;

    if (isDefined(threshold) && count > threshold) {
      const messages = [
        'The number of items in this list exceeds the threshold of {0}.',
        'Please use the search functionality, by typing at least 2 characters, to filter the list.',
      ];
      const message = isDefined(thresholdWarningMessage)
        ? thresholdWarningMessage
        : formatString(messages.join(' '), formatNumber(threshold));

      Component = (
        <div className="flex flex-col">
          <AsyncSelect
            {...props}
            captureMenuScroll={false}
            components={{
              Group,
              GroupHeading,
              MenuList: VirtualMenuList,
              Option: useCheckboxOption ? CheckboxOption : Option,
              ...components,
            }}
            loadOptions={filterOptions}
          />
          <div className="mt-3">
            <Alert
              rounded="lg"
              status="warning"
              variant="subtle">
              <AlertIcon />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }
  }

  return Component;
}
